import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

const PaymentResultPage = () => {
    const { t } = useTranslation();
    const { api } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('verifying'); // verifying | success | failed
    const [detail, setDetail] = useState('');
    const verifiedRef = useRef(false);

    useEffect(() => {
        if (verifiedRef.current) return;
        verifiedRef.current = true;

        const paymentId = searchParams.get('id');
        const transactionId = searchParams.get('transaction_id');
        const moyasarStatus = searchParams.get('status');

        const verify = async () => {
            if (!paymentId || !transactionId) {
                setStatus('failed');
                setDetail(t('subscription.resultFailedDesc'));
                return;
            }
            if (moyasarStatus && moyasarStatus !== 'paid') {
                setStatus('failed');
                setDetail(searchParams.get('message') || t('subscription.resultFailedDesc'));
                return;
            }
            try {
                const res = await api.get('/payments/verify', {
                    params: { payment_id: paymentId, transaction_id: transactionId },
                });
                if (res.data.status === 'success') {
                    setStatus('success');
                } else {
                    setStatus('failed');
                    setDetail(res.data.detail || t('subscription.resultFailedDesc'));
                }
            } catch (e) {
                setStatus('failed');
                setDetail(e.response?.data?.detail || t('subscription.resultFailedDesc'));
            }
        };
        verify();
    }, [api, searchParams, t]);

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="payment-result-page">
            <div className="w-full max-w-md text-center bg-white rounded-3xl border border-stone-200 shadow-xl p-10">
                {status === 'verifying' && (
                    <div data-testid="result-verifying">
                        <Loader2 className="w-14 h-14 animate-spin text-teal-500 mx-auto mb-6" />
                        <h2 className="text-xl font-bold text-stone-800">
                            {t('subscription.resultVerifying')}
                        </h2>
                    </div>
                )}

                {status === 'success' && (
                    <div data-testid="result-success">
                        <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="w-10 h-10 text-teal-500" />
                        </div>
                        <h2 className="text-2xl font-bold text-stone-800 mb-2">
                            {t('subscription.resultSuccessTitle')}
                        </h2>
                        <p className="text-stone-500 mb-8">{t('subscription.resultSuccessDesc')}</p>
                        <Button
                            onClick={() => navigate('/dashboard')}
                            className="w-full h-12 rounded-xl bg-teal-500 hover:bg-teal-600 text-white"
                            data-testid="go-dashboard-btn"
                        >
                            {t('subscription.goToDashboard')}
                        </Button>
                    </div>
                )}

                {status === 'failed' && (
                    <div data-testid="result-failed">
                        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                            <XCircle className="w-10 h-10 text-red-500" />
                        </div>
                        <h2 className="text-2xl font-bold text-stone-800 mb-2">
                            {t('subscription.resultFailedTitle')}
                        </h2>
                        <p className="text-stone-500 mb-8">{detail || t('subscription.resultFailedDesc')}</p>
                        <Button
                            onClick={() => navigate('/subscription')}
                            className="w-full h-12 rounded-xl bg-teal-500 hover:bg-teal-600 text-white"
                            data-testid="retry-btn"
                        >
                            {t('subscription.retry')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentResultPage;
